import { useEffect, useMemo, useRef, useState } from "react";
import { ImagePreview, ImageSizeSetting } from "./ImagePreview";
import { useModal, useModalRef } from "../providers/modal";
import { Button } from "./Button";

const sizes = [
  {
    maxHeight: "80vh",
    maxWidth: "90vw",
    z: 1,
  },
  {
    maxHeight: "130vh",
    maxWidth: "130vw",
    z: 1,
  },
  {
    maxHeight: "200vh",
    maxWidth: "200vw",
    z: 1,
  },
  {
    maxHeight: "300vh",
    maxWidth: "300vw",
    z: 1,
  },
  {
    maxHeight: "1000vh",
    maxWidth: "1000vw",
    z: 1,
  },
];
const ImageModal = ({
  rounded,
  alt,
  image,
}: {
  rounded: boolean;
  alt: string;
  image?: { path?: string; ref?: string; name?: string };
}) => {
  const setContent = useModal();
  const { containerRef } = useModalRef();
  const imageRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState(0);
  useEffect(() => {
    setZoom(0);
  }, [image]);
  const lastClickRatio = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const id = setTimeout(() => {
      if (containerRef?.current) {
        const scrollableWidth =
          containerRef.current.scrollWidth - containerRef.current.clientWidth;
        const scrollableHeight =
          containerRef.current.scrollHeight - containerRef.current.clientHeight;

        setContent((content) => ({
          component: null,
          ...content,
          justify: scrollableWidth ? undefined : "center",
        }));
        containerRef.current.scrollTo({
          left: zoom ? scrollableWidth * lastClickRatio.current.x : 0,
          top: zoom ? scrollableHeight * lastClickRatio.current.y : 0,
        });
      }
    }, 0);
    return () => clearTimeout(id);
  }, [zoom, containerRef?.current]);
  return (
    <ImagePreview
      rounded={rounded}
      transition="none"
      imageRef={imageRef}
      alt={alt}
      image={image}
      size={sizes[zoom]}
      useImageSize="size-full"
      cursor={zoom === sizes.length - 1 ? "zoom-out" : "zoom-in"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (imageRef.current) {
          const imagePosition = imageRef.current.getBoundingClientRect();
          lastClickRatio.current = {
            x: (e.clientX - imagePosition.left) / imagePosition.width,
            y: (e.clientY - imagePosition.top) / imagePosition.height,
          };
        }
        setZoom((zoom) => (zoom < sizes.length - 1 ? zoom + 1 : 0));
      }}
    />
  );
};

export const ImageCarousel = ({
  images,
  alt,
  size = { height: 20, z: 1 },
  hoverSize = size,
}: {
  alt: string;
  images?: { path?: string; ref?: string; name?: string }[];
  size?: ImageSizeSetting;
  hoverSize?: ImageSizeSetting;
}) => {
  const setContent = useModal();
  const [imageIdx, setImageIdx] = useState(0);
  const [modalImageIdx, setModalImageIdx] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const length = images?.length;
  const image = images?.[imageIdx];
  const modalImage = images?.[modalImageIdx];

  const { component, Pager } = useMemo(() => {
    const Pager = ({
      direction,
      fromModal,
    }: {
      direction: "next" | "prev";
      fromModal?: boolean;
    }) => {
      return length && length > 1 ? (
        <Button
          btnColor="accent"
          btnType="nav"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            (fromModal ? setModalImageIdx : setImageIdx)(
              (idx) =>
                (direction === "next" ? idx + length + 1 : idx + length - 1) %
                length
            );
          }}
        >
          {direction === "next" ? "Next" : "Prev"}
        </Button>
      ) : null;
    };

    return {
      component: (
        <div className="h-fit py-20 px-6 flex justify-evenly text-textSecondary">
          <div className="fixed left-10 bottom-1/2 z-10">
            <Pager direction="prev" fromModal />
          </div>
          <ImageModal
            rounded
            {...{
              alt,
              image: modalImage,
            }}
          />
          <div className="fixed right-10 bottom-1/2 z-10">
            <Pager direction="next" fromModal />
          </div>
        </div>
      ),
      Pager,
    };
  }, [length, modalImage]);
  useEffect(() => {
    if (open && image) {
      setContent((content) => {
        if (!content?.component) {
          setOpen(false);
          return content;
        } else {
          return {
            component,
            justify: "center",
          };
        }
      });
    }
  }, [image, open, component]);

  return length ? (
    <div>
      <div className={"flex justify-evenly "}>
        <ImagePreview
          objectFit="contain"
          rounded
          alt={alt}
          onClick={() => {
            setContent({
              component,
              justify: "center",
            });
            setModalImageIdx(imageIdx);
            setOpen(true);
          }}
          size={size}
          cursor="zoom-in"
          hoverSize={hoverSize}
          image={image}
        />
      </div>
      {length > 1 ? (
        <div className="flex justify-evenly text-textSecondary mt-4">
          <Pager direction="prev" />
          <Pager direction="next" />
        </div>
      ) : null}
    </div>
  ) : null;
};
